export const PROC_FREQ_VALUES = ['daily', 'weekly', 'monthly']

// Per-step KPI scoring is temporarily OFF (Teo 2026-06-02): procedures use
// the existing procedure-level KPI fields only. Flip to true to re-enable the
// per-step points + "de mai multe ori" quantity control + completion bonus.
export const STEP_KPI_ENABLED = false

export function getInitials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

export function isImageAttachment(att) {
    return Boolean(att?.type?.startsWith('image/') || /\.(png|jpe?g|gif|webp|avif|bmp)$/i.test(String(att?.url || att?.name || '')))
}

export function normalizeImageUrl(url) {
    if (!url) return ''
    const value = String(url).trim()
    if (/^https?:\/\//i.test(value)) return value
    if (value.startsWith('/')) return value
    return value
}

export function extractLegacyCommentImages(text) {
    const images = []
    const lines = String(text || '').split(/\n+/)
    for (const line of lines) {
        const match = line.match(/imagine\s+inc[aă]rcat[aă]:\s*(.*?)\s+(https?:\/\/\S+|\/\S+)/i)
        if (!match) continue
        const url = normalizeImageUrl(match[2])
        images.push({
            id: `legacy-${url}`,
            name: match[1]?.trim() || 'Imagine',
            url,
            type: 'image/*',
        })
    }
    return images
}

export function stripLegacyImageLines(text) {
    return String(text || '')
        .split(/\n+/)
        .filter((line) => !/^\s*imagine\s+inc[aă]rcat[aă]:/i.test(line))
        .join('\n')
        .trim()
}

export function getProcedureLabel(procedure, index) {
    const position = typeof index === 'number' ? index + 1 : ((procedure?.position ?? 0) + 1)
    return `${position}. ${procedure.title}`
}

export function deriveCardDetailExternalState({ currentCardId, eventDetail }) {
    if (!currentCardId || !eventDetail || String(eventDetail.cardId) !== String(currentCardId)) {
        return { applies: false }
    }

    const patch = eventDetail.patch || {}
    const result = { applies: true }
    if (Object.prototype.hasOwnProperty.call(patch, 'is_completed')) {
        result.completionValue = Boolean(patch.is_completed)
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'timer_started_at')) {
        result.timerValue = patch.timer_started_at ?? null
    }
    return result
}

export function groupTimeLogs(timeLogs) {
    if (!Array.isArray(timeLogs) || timeLogs.length === 0) return []
    const asc = [...timeLogs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    const windowMs = 15 * 60 * 1000
    const groups = []
    let current = null
    for (const log of asc) {
        const at = new Date(log.created_at).getTime()
        if (current && current.user_id === log.user_id && (at - current._lastTime) <= windowMs) {
            current.duration_minutes += Number(log.duration_minutes) || 0
            current._lastTime = at
            current._chunks += 1
        } else {
            current = {
                id: log.id,
                user_id: log.user_id,
                duration_minutes: Number(log.duration_minutes) || 0,
                created_at: log.created_at,
                description: log.description,
                _lastTime: at,
                _chunks: 1,
            }
            groups.push(current)
        }
    }
    return groups.reverse()
}

export function formatTimer(seconds) {
    if (!seconds) return '00:00:00'
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0')
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0')
    const s = (seconds % 60).toString().padStart(2, '0')
    return `${h}:${m}:${s}`
}

export function formatDurationSeconds(totalSeconds) {
    const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0))
    if (seconds <= 0) return '0s'

    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60

    if (h > 0) {
        return s > 0 ? `${h}h ${m}m ${s}s` : (m > 0 ? `${h}h ${m}m` : `${h}h`)
    }
    if (m > 0) {
        return s > 0 ? `${m}m ${s}s` : `${m}m`
    }
    return `${s}s`
}

export function completedDurationLabel(timeLogs, card) {
    const trackedSeconds = Math.floor(Number(card?.tracked_seconds) || 0)
    if (trackedSeconds > 0) return formatDurationSeconds(trackedSeconds)

    const totalMinutes = (Array.isArray(timeLogs) ? timeLogs : [])
        .reduce((acc, log) => acc + (Number(log.duration_minutes) || 0), 0)
        || Math.round((Number(card?.logged_hours) || 0) * 60)
    return formatDurationSeconds(totalMinutes * 60)
}

export function hasCardWorkStarted(card, {
    effectiveTimerStartedAt = card?.timer_started_at,
    timeLogs = [],
} = {}) {
    return Boolean(
        effectiveTimerStartedAt
        || Number(card?.tracked_seconds || 0) > 0
        || Number(card?.logged_minutes || 0) > 0
        || Number(card?.logged_hours || 0) > 0
        || (timeLogs || []).length > 0
        || (card?.card_completions || []).length > 0,
    )
}
